import * as React from 'react';
import { CreateEditingStyleInput } from '../../types';
import { useCreateEditingStyleMutation } from '../queries/useCreateEditingStyleMutation';
import { readValidationMessages, ValidationMessage } from '../../../../shared/validationErrors';
import { useTelemetryContext } from '../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../telemetry/utils';
import { useUserContext } from '../../../../shared/context/user-context';

export type CreateEditingStyleSuccess = {
    name: string;
};

export type CreateEditingStyleFailure = {
    name: string;
    messages: ValidationMessage[];
};

type CreateEditingStyleState = {
    createEditingStyle: (input: CreateEditingStyleInput) => Promise<void>;
    createError: CreateEditingStyleFailure | null;
    dismissCreateError: () => void;
    createSuccess: CreateEditingStyleSuccess | null;
    dismissCreateSuccess: () => void;
};

export function useCreateEditingStyle(): CreateEditingStyleState {
    const { user } = useUserContext();
    const { logger } = useTelemetryContext();
    const requestCreate = useCreateEditingStyleMutation();
    const [createError, setCreateError] = React.useState<CreateEditingStyleFailure | null>(null);
    const [createSuccess, setCreateSuccess] = React.useState<CreateEditingStyleSuccess | null>(
        null
    );

    async function createEditingStyle(input: CreateEditingStyleInput): Promise<void> {
        try {
            const createdEditingStyle = await requestCreate(input);
            setCreateSuccess({ name: createdEditingStyle.name });
        } catch (error) {
            logger.logException(error, {
                origin: 'EditingStyles.CreateEditingStyle',
                userInfo: getUserInfo(user)
            });
            setCreateError({ name: input.name, messages: await readValidationMessages(error) });
        }
    }

    function dismissCreateError(): void {
        setCreateError(null);
    }

    function dismissCreateSuccess(): void {
        setCreateSuccess(null);
    }

    return {
        createEditingStyle,
        createError,
        dismissCreateError,
        createSuccess,
        dismissCreateSuccess
    };
}
